import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Kbd,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCheck } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { cn } from '@ais-ui/utils';
import { useAISMapContext } from './AISMapContext';
import { useSelectionState } from '../../components/SelectionStateProvider';

interface ChooseTargetCardProps {
  className?: string;
}

export const ChooseTargetCard = ({ className }: ChooseTargetCardProps) => {
  const {
    onChooseTarget,
    onRejectTarget,
    chooseTargetTypes,
    showChooseTargetPanel,
  } = useAISMapContext();

  const { targetInfoDetailsList } = useSelectionState();

  const isAllowedTargetTypeSelected = useMemo(() => {
    if (targetInfoDetailsList.length === 0) {
      return false;
    }

    return chooseTargetTypes.includes(
      targetInfoDetailsList[0].extractedTargetInfoType,
    );
  }, [chooseTargetTypes, targetInfoDetailsList]);

  const finishButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && finishButtonRef.current) {
        finishButtonRef.current.click();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  if (
    !onChooseTarget.current ||
    !onRejectTarget.current ||
    !showChooseTargetPanel
  ) {
    return null;
  }

  return (
    <Card>
      <div className="flex justify-between">
        <CardHeader className="pb-0 w-64">
          <CardTitle className="text-md">Choose target</CardTitle>
          <CardDescription>
            Select a target on the map to populate your data
          </CardDescription>
        </CardHeader>
        <Button
          className=""
          id="choosetarget-reject-button"
          onClick={onRejectTarget.current}
          variant="ghost"
          size="icon"
        >
          <XIcon className="size-4" />
        </Button>
      </div>
      <div
        className={cn('w-fit h-fit grid grid-cols-2 gap-4 p-4 m-0', className)}
      >
        <div className="flex space-x-2 items-center">
          <Button
            id="choosetarget-reject-button"
            onClick={onRejectTarget.current}
            variant="ghost"
            size="icon"
          >
            <XIcon className={cn('size-5')}></XIcon>
          </Button>
          <Label>Reject</Label>
        </div>
        <div className="flex space-x-2 items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                ref={finishButtonRef}
                id="choosetarget-finish-button"
                disabled={!isAllowedTargetTypeSelected}
                onClick={() => {
                  if (onChooseTarget.current) {
                    onChooseTarget.current(
                      targetInfoDetailsList[0].extractedTargetInfo[0],
                    );
                  } else {
                    console.error('onChooseTarget is not available');
                  }
                }}
                variant="ghost"
                size="icon"
              >
                <PiCheck
                  className={cn(
                    'size-5',
                    isAllowedTargetTypeSelected && 'text-green-600',
                  )}
                ></PiCheck>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="flex space-x-2">
              Finish, use <Kbd className="text-xs mx-2 p-0">Enter</Kbd>
            </TooltipContent>
          </Tooltip>
          <Label>Choose</Label>
        </div>
      </div>
    </Card>
  );
};
